import PageHeader from '@atlaskit/page-header';
import { token } from '@atlaskit/tokens';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type SendDatabaseCreatedViewEventData } from '@databases/analytics/src/types';
import { getUniqueViewName } from '@databases/api-views/utils/viewName';
import { getGroupField, getSubgroupField } from '@databases/grouping';
import { isMobile } from '@databases/modes';
import usePanel from '@databases/panel/usePanel';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { SkeletonItem } from '@databases/skeleton';
import { ViewId } from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { getDatabaseFields } from '@databases/utils/database/utils';
import { createView } from '@databases/utils/database/view/view';
import ViewSettingsButtons from '@databases/view-settings-buttons';
import ViewTabs from '@databases/view-tabs/ViewTabs';
import { ViewPanelSettingType } from '@databases/views/types';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import afterFrame from 'afterframe';
import React, { type FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';

import DatabaseActions, { type DatabaseActionKey } from '../database/DatabaseActions';
import DatabaseNameInlineEdit from '../database-name/DatabaseNameInlineEdit';
import { ButtonGroup } from '../styled';

import ViewPicker from './view-picker/ViewPicker';
import DatabaseSearch from './view-settings-picker/DatabaseSearch';

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
export const Container = styled.div`
  display: flex;
  position: relative;
  overflow: hidden;
  align-items: center;
  padding-top: ${token('space.100')};
  padding-bottom: ${token('space.100')};
  column-gap: 100px;
  overflow-x: auto;
  margin-right: ${token('space.100')};
  margin-left: ${token('space.100')};
  padding-left: ${token('space.025')};
  padding-right: ${token('space.025')};

  > h2 {
    margin-right: ${token('space.050')};
    min-width: 0;
  }

  > .expand {
    flex-grow: 1;
  }

  > :last-child {
    flex-shrink: 0;
  }
`;

const ContainerMobile = styled(Container)`
  column-gap: ${token('space.0', '0px')};
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: ${token('space.050', '4px')};
  overflow: auto;
  flex-shrink: 1;
  min-width: 250px;
  width: 100%;

  > div:first-child {
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
  }
`;

export interface ToolbarProps {
  contentId: string;
  actions?: DatabaseActionKey[];
  canEditContent: boolean;
  canEditFields: boolean;
  showViewSettings?: boolean;
  showName?: boolean;
}

/**
 * Renders a table that allows to edit database values using inline edit fields and to add fields and add entries to a
 * database.
 */
const Toolbar: FunctionComponent<ToolbarProps> = ({
  contentId,
  actions,
  canEditContent,
  canEditFields,
  showViewSettings = true,
  showName = true,
}) => {
  const [isViewsMenuOpen, setIsViewsMenuOpen] = useState(false);

  const { view, setViewId, resolvedView, clearUnsavedView } = useView();

  const client = useAnalytics();
  const dispatch = defaultStoreUtils.useDispatch();
  const { setActivePanelSetting } = useViewUiState();
  const intl = useIntl();
  const { showPanel } = usePanel();

  const { savedViews, fields, groupField, subgroupField } =
    defaultStoreUtils.useEqualSelector((state) => {
      const databaseDraft = selectDatabaseDraftByContentId(state, {
        contentId,
        includeDeleted: true,
      });
      const groupField = getGroupField(databaseDraft, view.settings);
      const subgroupField = getSubgroupField(databaseDraft, view.settings);
      return {
        savedViews: databaseDraft.views,
        fields: getDatabaseFields(databaseDraft),
        groupField,
        subgroupField,
      };
    });

  const showDatabaseActions = isPluginEnabled(PluginKey.DATABASE_ACTIONS);

  const ContainerComponent = isMobile() ? ContainerMobile : Container;

  const openViewSettingsPanel = useCallback(
    (activeViewSetting: ViewPanelSettingType | null) => {
      const startedRenderingAt = performance.now();
      setActivePanelSetting(activeViewSetting);
      showPanel('ViewSettingsPanel', {
        canEditViews: canEditFields,
      });

      afterFrame(() => {
        const reliability = ReliabilityMetrics.getInstance();
        reliability.successWithDuration(
          MetricName.DATABASE_PANEL_VIEW_SETTINGS_OPEN,
          startedRenderingAt,
          performance.now()
        );
      });

      // If table layout header cell selection is active,
      // remove selection border on view settings button click
      dispatch(deselectAll());
    },
    [setActivePanelSetting, showPanel, canEditFields, dispatch]
  );

  const handleAddView = useCallback(
    (source: SendDatabaseCreatedViewEventData['source']) => {
      let newView = createView();

      newView = {
        ...newView,
        name: getUniqueViewName(
          newView,
          savedViews.map((v) => v.name),
          intl
        ),
      };

      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.ADD_SAVED_VIEW,
          view: newView,
        })
      );

      if (view.id === ViewId.UNSAVED) {
        clearUnsavedView();
      }

      setViewId(newView.id);

      openViewSettingsPanel(ViewPanelSettingType.OVERVIEW);

      client.sendDatabaseCreateViewEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId: newView.id,
        source,
        layoutType: newView.settings.layout.type,
      });
    },
    [
      clearUnsavedView,
      client,
      contentId,
      dispatch,
      intl,
      openViewSettingsPanel,
      savedViews,
      setViewId,
      view.id,
    ]
  );

  return (
    <>
      {showName && isPluginEnabled(PluginKey.INLINE_DATABASE_NAME) && (
        <PageHeader>
          <DatabaseNameInlineEdit contentId={contentId} isReadonly={!canEditContent} />
        </PageHeader>
      )}

      <ContainerComponent>
        <InnerContainer>
          <SkeletonItem isButton>
            <ViewTabs
              contentId={contentId}
              defaultView={resolvedView}
              isReadonly={!canEditFields}
              openViewSettingsPanel={openViewSettingsPanel}
              handleAddView={handleAddView}
              showViewSettings={showViewSettings}
              ViewPicker={ViewPicker}
              isViewsMenuOpen={isViewsMenuOpen}
              setIsViewsMenuOpen={setIsViewsMenuOpen}
            />
          </SkeletonItem>
        </InnerContainer>
        <ButtonGroup isCompact>
          {showViewSettings && (
            <DatabaseSearch contentId={contentId} value={view.settings} showQuickSearch />
          )}

          {showDatabaseActions && (
            <SkeletonItem isButton>
              <DatabaseActions
                contentId={contentId}
                placement="bottom-end"
                show={actions}
                isReadOnly={!canEditContent}
              />
            </SkeletonItem>
          )}
        </ButtonGroup>
      </ContainerComponent>

      {showViewSettings && (
        <ViewSettingsButtons
          hasNoFields={!fields}
          openViewSettingsPanel={openViewSettingsPanel}
          groupField={groupField}
          subgroupField={subgroupField}
        />
      )}
    </>
  );
};

export default Toolbar;
